import { collection, doc, getDocs, query, setDoc, where, Timestamp, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { DailyQuest, DailyQuestActivity, DAILY_QUESTS } from '../models/DailyQuest';
import { Lesson, Word } from '../models/Lesson';
import { ActivityLog, Exercise, ExerciseType } from '../models/LearnTypes';
// No need to import the LessonService type since we're using dynamic imports

export class DailyQuestService {
  constructor() {}

  /**
   * Get all available daily quests for a user
   */
  async getDailyQuestsForUser(userId: string): Promise<DailyQuest[]> {
    // Ensure we have a valid user ID before proceeding
    if (!userId) {
      console.warn('Attempted to fetch daily quests without a valid user ID');
      return [];
    }

    // Get base quest definitions
    const quests = [...DAILY_QUESTS];
    
    try {
      // Check which quests are completed today
      const completedQuests = await this.getCompletedQuestsToday(userId);
      const completedQuestIds = new Set(completedQuests.map(q => q.questId));
      
      console.log(`🎯 [DailyQuest] Found ${completedQuests.length} completed quests for today`);
      
      // Generate available quests
      const availableQuests: DailyQuest[] = [];
      
      for (const quest of quests) {
        const isCompleted = completedQuestIds.has(quest.id);
        console.log(`🎯 [DailyQuest] Quest ${quest.id} (${quest.type}): isCompleted=${isCompleted}`);
        
        // Generate quest content
        let questLesson: Lesson | null = null;
        // Default to true for availability - we'll set to false only if generation fails
        let isAvailable = true;
        
        if (!isCompleted) {
          if (quest.type === 'word-randomizer') {
            try {
              console.log(`🎯 [DailyQuest] Generating word-randomizer quest: ${quest.id}`);
              questLesson = await this.generateWordRandomizerQuest(userId, quest);
              
              if (!questLesson) {
                console.log(`🎯 [DailyQuest] Failed to generate lesson for quest ${quest.id} - not enough words available`);
                isAvailable = false;
              } else {
                console.log(`🎯 [DailyQuest] Successfully generated lesson for quest ${quest.id}`);
              }
            } catch (error) {
              console.error(`Error generating ${quest.type} quest:`, error);
              isAvailable = false;
              // Continue to next quest if there's an error generating this one
              continue;
            }
          } else {
            // For other quest types, assume they're available by default if not completed
            console.log(`🎯 [DailyQuest] Quest type ${quest.type} doesn't need lesson generation`);
          }
        } else {
          // If quest is completed, it's not available
          isAvailable = false;
        }
        
        availableQuests.push({
          ...quest,
          lesson: questLesson || undefined,
          lastCompletedAt: isCompleted ? new Date() : null,
          isAvailable: isAvailable
        });
        
        console.log(`🎯 [DailyQuest] Added quest ${quest.id} with isAvailable=${isAvailable}, lastCompletedAt=${isCompleted ? 'today' : 'null'}`);
        // If the quest is available, it should be clickable in the UI
      }
      
      return availableQuests;
    } catch (error) {
      console.error('Error generating daily quests:', error);
      // Return empty quests list if there's an error
      return [];
    }
  }

  /**
   * Generate a word randomizer quest lesson
   */
  private async generateWordRandomizerQuest(
    userId: string, 
    questConfig: Omit<DailyQuest, 'lesson' | 'lastCompletedAt' | 'isAvailable'>
  ): Promise<Lesson | null> {
    // Import the lessonService lazily to avoid circular dependencies
    const { lessonService } = await import('./lessons/LessonService');
    
    // Get all active lessons for the user
    const lessons = await lessonService.getUserLessons(userId);
    const activeLessons = lessons.filter(lesson => lesson.active);
    
    console.log(`🎯 [DailyQuest] Found ${activeLessons.length} active lessons`);
    
    // Extract all words from active lessons
    const allWords: { word: string; translation: string }[] = [];
    const uniqueWords = new Set<string>();
    
    // Also try to extract words directly from the lesson's words array if available
    for (const lesson of activeLessons) {
      // Try getting words from the lesson's words array (primary source)
      if (lesson.words && lesson.words.length > 0) {
        console.log(`🎯 [DailyQuest] Found ${lesson.words.length} words in lesson ${lesson.name}`);
        
        for (const word of lesson.words) {
          if (word.word && word.translation && !uniqueWords.has(word.word)) {
            allWords.push({
              word: word.word,
              translation: word.translation
            });
            uniqueWords.add(word.word);
          }
        }
      }
      
      // Fallback: extract from exercises if no words array or to supplement it
      if (!lesson.levels) continue;
      
      for (const level of lesson.levels) {
        if (!level.exercises) continue;
        
        for (const exercise of level.exercises) {
          if (exercise.word && exercise.translation && !uniqueWords.has(exercise.word)) {
            allWords.push({
              word: exercise.word,
              translation: exercise.translation
            });
            uniqueWords.add(exercise.word);
          }
        }
      }
    }
    
    console.log(`🎯 [DailyQuest] Total unique words found: ${allWords.length}. Required: ${questConfig.requiredWordCount}`);
    
    // Check if we have enough words - reduce the requirement if needed
    const minRequiredWords = Math.min(questConfig.requiredWordCount, 3); // At least 3 words
    
    if (allWords.length < minRequiredWords) {
      console.log(`🎯 [DailyQuest] Not enough words found (${allWords.length}). Need at least ${minRequiredWords}`);
      return null;
    }
    
    // Adjust requiredWordCount if we don't have enough words
    const actualWordCount = Math.min(questConfig.requiredWordCount, allWords.length);
    console.log(`🎯 [DailyQuest] Will use ${actualWordCount} words for the quest`);
    
    // Randomly select unique words - use actual available count instead of the full requirement
    const selectedWords = this.getRandomUniqueItems(allWords, actualWordCount);
    console.log(`🎯 [DailyQuest] Selected ${selectedWords.length} unique words for the quest`);
    
    // Create a quest "lesson" structure
    return {
      id: `daily-quest-${questConfig.id}`,
      name: questConfig.name,
      description: questConfig.description,
      words: selectedWords.map(w => ({ 
        id: `daily-quest-word-${Math.random().toString(36).substring(2, 9)}`,
        word: w.word,
        translation: w.translation,
        category: 'daily-quest'
      })) as Word[],
      levels: this.createLevelsFromWords(selectedWords),
      isCompleted: false,
      isLocked: false,
      isDailyQuest: true,
      dailyQuestType: questConfig.type,
      dailyQuestBonus: questConfig.bonusXP,
      completedAt: null,
      active: true,
      order: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  /**
   * Create level structure from words
   */
  private createLevelsFromWords(words: { word: string; translation: string }[]): any[] {
    const exerciseTypes: ExerciseType[] = ['match', 'flashcard', 'reverse-flashcard', 'spelling'];
    const levels = [];
    
    // Create exercises for each word
    for (let i = 0; i < words.length; i++) {
      const { word, translation } = words[i];
      
      // Cycle through exercise types
      const exerciseType = exerciseTypes[i % exerciseTypes.length];
      
      const exercises: Exercise[] = [{
        id: `daily-quest-exercise-${i}`,
        type: exerciseType,
        word: word,
        translation: translation
      }];
      
      // Create level
      levels.push({
        id: `daily-quest-level-${i}`,
        type: exerciseType,
        title: `Daily Quest - ${exerciseType}`,
        description: `Practice ${word}`,
        exercises: exercises,
        xpReward: 5,
        state: 'not-started'
      });
    }
    
    return levels;
  }

  /**
   * Get random unique items from an array
   */
  private getRandomUniqueItems<T>(items: T[], count: number): T[] {
    if (items.length <= count) return [...items];
    
    const shuffled = [...items].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }

  /**
   * Get completed quests for today
   */
  async getCompletedQuestsToday(userId: string): Promise<DailyQuestActivity[]> {
    if (!userId) return [];
    
    try {
      console.log(`🎯 [DailyQuest] Checking completed quests for user: ${userId}`);
      
      // We need to use the same collection name as used in ActivityService: 'activityLogs'
      const activitiesRef = collection(db, 'activityLogs');
      
      console.log(`🎯 [DailyQuest] Using collection 'activityLogs' instead of 'activities'`);
      // We need to query both 'daily-quest-completed' and 'lesson-completed' with daily-quest lessonId
      // since it seems quests are being logged as 'lesson-completed'
      console.log(`🎯 [DailyQuest] Creating query to look for completed daily quests...`);
      
      const q = query(
        activitiesRef,
        where('userId', '==', userId),
        // We can't use OR conditions in Firestore, so we need to query more broadly
        // and filter in memory
      );
      
      console.log(`🎯 [DailyQuest] Query created with: collection='activityLogs', where userId == ${userId}`);
      const snapshot = await getDocs(q);
      
      console.log(`🎯 [DailyQuest] Got ${snapshot.docs.length} total activities from Firestore`);
      
      // Filter activities by date (today) and type (daily-quest-completed OR lesson-completed with daily-quest lessonId)
      const today = new Date();
      
      // Convert to properly typed activity objects
      const activities = snapshot.docs
        .filter(doc => {
          const data = doc.data();
          
          // Check if it's a daily quest completion
          const isDailyQuestCompleted = data.type === 'daily-quest-completed';
          
          // Check if it's a lesson completion with a daily quest lessonId
          const isLessonCompletedWithDailyQuestId = 
            data.type === 'lesson-completed' && 
            data.lessonId && 
            data.lessonId.startsWith('daily-quest-');
            
          const isRelevantActivity = isDailyQuestCompleted || isLessonCompletedWithDailyQuestId;
          
          if (isRelevantActivity) {
            console.log(`🎯 [DailyQuest] Found daily quest activity: type=${data.type}, lessonId=${data.lessonId || 'unknown'}`);
          }
          
          return isRelevantActivity;
        })
        .map(doc => {
          const data = doc.data();
          console.log(`🎯 [DailyQuest] Processing quest activity: ${data.lessonId || 'unknown'}`);
          // Extract the quest ID from the lessonId
          const questId = data.questId || 
                        (data.lessonId?.startsWith('daily-quest-') ? 
                          data.lessonId.replace('daily-quest-', '') : 
                          data.lessonId);
          
          // Determine quest type - use dailyQuestType if available, otherwise use a default
          const questType = data.dailyQuestType || 
                           (data.lessonId?.includes('word-randomizer') ? 'word-randomizer' : 'unknown');
          
          // Get bonus XP if available
          const bonusXP = data.dailyQuestBonus || data.perfectLessonBonus || 0;
          
          // Make sure logging happens before the return statement
          console.log(`🎯 [DailyQuest] Processed activity: questId=${questId}, questType=${questType}`);
          
          return {
            id: doc.id,
            questId,
            questType,
            completedAt: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp),
            xpEarned: data.xpEarned,
            bonusXP,
            isPerfectLesson: data.isPerfectLesson || false,
            userId: data.userId
          };
        });
      
      // Then filter by date
      return activities.filter(activity => {
        if (!activity.completedAt) return false;
        const activityDate = new Date(activity.completedAt);
        return activityDate.toDateString() === today.toDateString();
      }) as DailyQuestActivity[];
    } catch (error) {
      console.error('Error fetching completed quests:', error);
      return [];
    }
  }

  /**
   * Mark a quest as completed
   */
  async markQuestCompleted(
    userId: string,
    questId: string,
    questType: string,
    xpEarned: number,
    bonusXP: number,
    isPerfectLesson: boolean = false
  ): Promise<string> {
    // Check if user ID is valid
    if (!userId) {
      throw new Error('Invalid user ID');
    }
    
    // Create the activity data with all necessary fields so it can be identified by our query
    const activityData = {
      userId,
      type: 'daily-quest-completed', // We'll keep this type for consistency, but our query will match both types
      lessonId: `daily-quest-${questId}`,
      lessonName: `Daily Quest: ${questType}`,
      xpEarned,
      dailyQuestType: questType,
      dailyQuestBonus: bonusXP,
      isPerfect: isPerfectLesson,
      isPerfectLesson,
      questId // Store the original quest ID for reference
    };
    
    console.log(`🎯 [DailyQuest] Logging daily quest completion to 'activityLogs' collection`);
    console.log(`🎯 [DailyQuest] Activity details:`);
    console.log(`- Type: 'daily-quest-completed'`); 
    console.log(`- LessonId: 'daily-quest-${questId}'`);
    console.log(`- QuestId: '${questId}'`);
    console.log(`- QuestType: '${questType}'`);
    
    const docRef = await addDoc(collection(db, 'activityLogs'), {
      ...activityData,
      timestamp: serverTimestamp()
    });
    
    console.log(`🎯 [DailyQuest] Activity logged successfully with ID: ${docRef.id}`);
    
    return docRef.id;
  }

  /**
   * Check if a quest is available today
   */
  async isQuestAvailableToday(userId: string, questId: string): Promise<boolean> {
    try {
      const completedQuests = await this.getCompletedQuestsToday(userId);
      return !completedQuests.some(q => q.questId === questId);
    } catch (error) {
      console.error('Error checking quest availability:', error);
      // If there's an error, we'll assume the quest is available
      // This prevents blocking the feature due to permission issues
      return true;
    }
  }
}

// Create the service instance using a more deterministic initialization pattern
// This helps prevent initialization order issues in production builds
let service: DailyQuestService | null = null;
export const dailyQuestService = (() => {
  if (!service) {
    service = new DailyQuestService();
  }
  return service;
})();
